import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { BookOpen, Building2, ChevronRight, ClipboardCheck, Command, CreditCard, History, Inbox, Mail, Receipt, Settings, ShieldCheck, ShoppingCart, Star, UserPlus, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';
import { CommsPauseBanner } from '@/components/admin/CommsPause';
import { AccountPalette } from '@/components/admin/AccountPalette';
import type { AdminAccountRef } from '@/hooks/admin/useAdminAccounts';

interface AdminTab {
  to: string;
  label: string;
  icon: LucideIcon;
  /** One-line "what lives here" — shown as the header subtitle on this tab. */
  description: string;
}

interface AdminNavGroup {
  label: string;
  tabs: AdminTab[];
}

/**
 * The superadmin nav, organized into labeled groups so the tab row reads as
 * sections instead of a flat wall of ten pills. The active tab also drives the
 * page header (breadcrumb + title + subtitle) so it's always clear where you
 * are.
 */
const NAV_GROUPS: AdminNavGroup[] = [
  {
    label: 'Orders',
    tabs: [
      {
        to: '/admin/quick-order',
        label: 'Quick order',
        icon: ShoppingCart,
        description: 'Take a phone order — pick a customer and open their cart.',
      },
      // Emailed-PO review queue (EDI Orders & Invoicing).
      {
        to: '/admin/edi',
        label: 'EDI orders',
        icon: Inbox,
        description: 'Emailed purchase orders — review, match lines, and submit.',
      },
      // Staff-forwarded customer emails read into proposed orders.
      {
        to: '/admin/order-desk',
        label: 'Order desk',
        icon: Mail,
        description: 'Forwarded customer emails, read into an order — accept or edit.',
      },
    ],
  },
  {
    label: 'Customers',
    tabs: [
      {
        to: '/admin/customers',
        label: 'Customers',
        icon: Building2,
        description: 'Enable, manage, and close customer accounts.',
      },
      {
        to: '/admin/onboarding',
        label: 'Onboarding',
        icon: UserPlus,
        description: 'Applications, invites, and chain/franchise new-store links.',
      },
    ],
  },
  {
    label: 'Money',
    tabs: [
      {
        to: '/admin/billing',
        label: 'Billing',
        icon: Receipt,
        description: 'Bulk statement and invoice billing runs.',
      },
      {
        to: '/admin/payments',
        label: 'Payments',
        icon: CreditCard,
        description: 'Stripe payouts, quick invoice sync, and returned payments.',
      },
    ],
  },
  {
    label: 'Oversight',
    tabs: [
      {
        to: '/admin/audits',
        label: 'Audits',
        icon: ClipboardCheck,
        description: 'Cylinder audits and driver access.',
      },
      {
        to: '/admin/activity',
        label: 'Activity',
        icon: History,
        description: 'Master log of orders, payments, changes, and communications.',
      },
      {
        to: '/admin/reviews',
        label: 'Reviews',
        icon: Star,
        description: 'How-did-we-do emails after service visits, and the ratings.',
      },
    ],
  },
  {
    label: 'Setup',
    tabs: [
      {
        to: '/admin/knowledge',
        label: 'Knowledge',
        icon: BookOpen,
        description: 'Mr. Bubbles knowledge base and voice teachings.',
      },
      // Staff (cross-app user management) lives on the GATEWAY hub at
      // alamedapointbg.com/admin.html — it drives this repo's admin-staff API.
      {
        to: '/admin/company',
        label: 'Company',
        icon: Settings,
        description: 'Billing emails, order fees, pricing, order desk, syncs and sign-up requests.',
      },
    ],
  },
];

/**
 * Resolve which tab (and group) the current URL lives under — longest prefix
 * wins, so /admin/customers/:id still lights up Customers and
 * /admin/audits/:id lights up Audits.
 */
function findActiveTab(pathname: string): { group: string; tab: AdminTab } | null {
  let best: { group: string; tab: AdminTab } | null = null;
  for (const group of NAV_GROUPS) {
    for (const tab of group.tabs) {
      const match = pathname === tab.to || pathname.startsWith(`${tab.to}/`);
      if (match && (!best || tab.to.length > best.tab.to.length)) {
        best = { group: group.label, tab };
      }
    }
  }
  return best;
}

/* -------------------- Palette context -------------------- */

interface AccountPaletteContextValue {
  /** Open the ⌘K account finder. Optionally override what selecting a row does. */
  openPalette: (onSelect?: (acct: AdminAccountRef) => void) => void;
  closePalette: () => void;
}

const AccountPaletteContext = createContext<AccountPaletteContextValue | null>(null);

/**
 * Lets any /admin page open the shared ⌘K account-finder and, optionally,
 * hook the selection. Falls back to a no-op outside the admin shell so callers
 * never crash.
 */
export function useAccountPalette(): AccountPaletteContextValue {
  return (
    useContext(AccountPaletteContext) ?? {
      openPalette: () => {},
      closePalette: () => {},
    }
  );
}

/** Should a keystroke be treated as "the user is typing in a field"? */
function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable
  );
}

export default function AdminLayout() {
  const { isSuperadmin, loading } = useIsSuperadmin();
  const { pathname } = useLocation();

  // Drives the header breadcrumb / title / subtitle.
  const active = useMemo(() => findActiveTab(pathname), [pathname]);

  const [paletteOpen, setPaletteOpen] = useState(false);
  // The per-open selection handler. null => default (navigate to the account's
  // user-management view).
  const [onSelect, setOnSelect] = useState<((acct: AdminAccountRef) => void) | null>(null);

  const openPalette = useCallback((handler?: (acct: AdminAccountRef) => void) => {
    setOnSelect(() => handler ?? null);
    setPaletteOpen(true);
  }, []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // Global ⌘K / Ctrl-K listener, scoped to the admin shell's lifetime.
  useEffect(() => {
    if (!isSuperadmin) return;
    function handler(e: KeyboardEvent) {
      const isPaletteKey = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isPaletteKey) {
        // ⌘K should open from anywhere, even mid-typing in a field.
        e.preventDefault();
        setOnSelect(null);
        setPaletteOpen((v) => !v);
        return;
      }
      // Bare "/" focus shortcut, but only when not already typing.
      if (e.key === '/' && !isEditableTarget(e.target) && !paletteOpen) {
        e.preventDefault();
        setOnSelect(null);
        setPaletteOpen(true);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isSuperadmin, paletteOpen]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-brix-muted">
        Loading…
      </div>
    );
  }
  if (!isSuperadmin) {
    // Not flagged — bounce to /home. Don't render a 403 page; admins are rare,
    // so any non-superadmin landing here is almost certainly a wrong URL.
    return <Navigate to="/home" replace />;
  }

  return (
    <AccountPaletteContext.Provider value={{ openPalette, closePalette }}>
      <div className="flex-1 flex flex-col overflow-hidden">
        <CommsPauseBanner />
        {/* Header row — centered to match tab content below */}
        <div className="border-b border-brix-border bg-brix-surface/60 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-6 pt-6 pb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                {/* Breadcrumb: Superadmin › group › tab — always says where you are. */}
                <div className="flex items-center gap-1.5 mb-1 text-xs tracking-tight text-brix-muted font-semibold">
                  <ShieldCheck size={13} className="text-brix-primary" />
                  Superadmin
                  {active && (
                    <>
                      <ChevronRight size={11} className="text-brix-muted/60" aria-hidden />
                      <span>{active.group}</span>
                      <ChevronRight size={11} className="text-brix-muted/60" aria-hidden />
                      <span className="text-brix-ink">{active.tab.label}</span>
                    </>
                  )}
                </div>
                <h1 className="font-display text-2xl font-semibold text-brix-ink">
                  {active?.tab.label ?? 'Settings'}
                </h1>
                <p className="text-sm text-brix-muted mt-1 max-w-2xl">
                  {active?.tab.description ??
                    'Onboard customers from QuickBooks, set up sign-in users, and send welcome emails.'}
                </p>
              </div>

              {/* ⌘K account finder affordance */}
              <button
                type="button"
                onClick={() => openPalette()}
                className={cn(
                  'group hidden sm:inline-flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer',
                  'border border-brix-border bg-brix-surface text-sm text-brix-muted',
                  'transition-all duration-200 hover:border-brix-primary/40 hover:text-brix-ink',
                )}
                title="Find an account (⌘K)"
              >
                <Command size={14} className="text-brix-primary" />
                Search accounts…
                <kbd className="ml-1 inline-flex items-center rounded border border-brix-border bg-brix-surface-alt px-1.5 py-0.5 text-[10px] font-mono text-brix-muted">
                  ⌘K
                </kbd>
              </button>
            </div>

            {/* Tabs — grouped clusters with section labels and dividers. */}
            <nav className="mt-5 flex items-end gap-4 overflow-x-auto scrollbar-thin pb-0.5">
              {NAV_GROUPS.map((group, i) => (
                <div
                  key={group.label}
                  className={cn('shrink-0', i > 0 && 'border-l border-brix-border pl-4')}
                >
                  <div className="px-1 mb-1 text-[10px] font-semibold uppercase tracking-wider text-brix-muted/80 select-none">
                    {group.label}
                  </div>
                  <div className="flex gap-1">
                    {group.tabs.map(({ to, label, icon: Icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) =>
                          cn(
                            'group inline-flex items-center gap-2 px-3.5 py-2 text-sm rounded-lg cursor-pointer whitespace-nowrap',
                            'transition-all duration-200',
                            isActive
                              ? 'bg-brix-primary-tint text-brix-primary font-semibold'
                              : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
                          )
                        }
                      >
                        <Icon size={14} strokeWidth={1.8} />
                        {label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab content — same max-width keeps tables visually aligned with the header */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </div>

        <AccountPalette
          open={paletteOpen}
          onClose={closePalette}
          onSelectAccount={onSelect ?? undefined}
        />
      </div>
    </AccountPaletteContext.Provider>
  );
}
